"use client"

import { motion } from "framer-motion"
import { MapPin, Phone, Mail, ExternalLink, MessageCircle } from "lucide-react"
import Link from "next/link"

const branches = [
  {
    id: "egypt-heliopolis",
    country: "مصر",
    countryEn: "Egypt",
    location: "مصر الجديدة",
    flag: "🇪🇬",
    isNew: false,
    address: "31 صالح حقي باشا - مصر الجديدة - المطار، القاهرة",
    taxId: "٨٢٥-٨١٠-٧٦٨",
    phone: "01201922224",
    whatsapp: ["01201922224"],
    email: "egypt@anmka.com",
    mapUrl: "https://maps.app.goo.gl/vtiDdwJXriraxBhT6?g_st=ic",
    social: {
      facebook: "https://www.facebook.com/people/Anmka-Egypt/61553542344778/",
      instagram: "https://www.instagram.com/anmka.egypt",
      tiktok: "https://www.tiktok.com/@anmka.egypt",
    },
  },
  {
    id: "egypt-nasr-city",
    country: "مصر",
    countryEn: "Egypt",
    location: "مدينة نصر",
    flag: "🇪🇬",
    isNew: false,
    address: "١١ حسين عفيفي متفرع من مكرم عبيد – مدينة نصر",
    phone: "+20 12 13117963",
    whatsapp: ["01213117963"],
    email: "egypt@anmka.com",
    mapUrl: "https://www.google.com/maps/search/?api=1&query=11+Hussein+Afifi,+Makram+Ebeid,+Nasr+City,+Cairo",
    social: {
      facebook: "https://www.facebook.com/people/Anmka-Egypt/61553542344778/",
      instagram: "https://www.instagram.com/anmka.egypt",
      tiktok: "https://www.tiktok.com/@anmka.egypt",
    },
  },
  {
    id: "saudi-jeddah",
    country: "السعودية",
    countryEn: "Saudi Arabia",
    flag: "🇸🇦",
    isNew: true,
    address: "الملك عبدالله - الشرفية - جدة",
    registrationId: "7034491360",
    phone: "+966538556544",
    whatsapp: ["+966538556544"],
    email: "ksa@anmka.com",
    website: "https://anmka.sa",
    social: {
      facebook: "https://www.facebook.com/share/1Mxhghbcub/?mibextid=wwXIfr",
      instagram: "https://www.instagram.com/anmka.ksa",
    },
  },
]

export function BranchesSection() {
  return (
    <section id="branches" className="px-6 py-24">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <p className="text-sm font-medium text-zinc-500 uppercase tracking-wider mb-4">فروعنا</p>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-zinc-100 mb-4">
            نخدمك في مصر والسعودية
          </h2>
          <p className="text-zinc-500 max-w-xl mx-auto text-balance">
            فريقنا متواجد في فروع متعددة لخدمتك بشكل أفضل وأسرع
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {branches.map((branch, index) => (
            <motion.div
              key={branch.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`relative p-6 rounded-2xl border ${
                branch.isNew
                  ? "bg-gradient-to-br from-zinc-900/50 to-green-900/20 border-green-700/30"
                  : "bg-zinc-900/50 border-zinc-800/50"
              } hover:border-zinc-700/50 transition-all duration-300`}
            >
              {/* New Badge */}
              {branch.isNew && (
                <div className="absolute -top-3 -start-3 px-3 py-1 bg-green-600 text-white text-xs font-medium rounded-full">
                  جديد!
                </div>
              )}

              {/* Header */}
              <div className="flex items-center gap-3 mb-6">
                <span className="text-4xl">{branch.flag}</span>
                <div>
                  <h3 className="font-heading text-xl font-semibold text-zinc-100">
                    فرع {branch.country}
                    {branch.location ? ` — ${branch.location}` : ""}
                  </h3>
                  <p className="text-sm text-zinc-500">{branch.countryEn}</p>
                </div>
              </div>

              {/* Details */}
              <div className="space-y-4">
                {/* Address */}
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-zinc-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm text-zinc-400">{branch.address}</p>
                    {branch.mapUrl && (
                      <Link
                        href={branch.mapUrl}
                        target="_blank"
                        className="text-xs text-zinc-500 hover:text-zinc-300 flex items-center gap-1 mt-1"
                      >
                        عرض على الخريطة
                        <ExternalLink className="w-3 h-3" />
                      </Link>
                    )}
                  </div>
                </div>

                {/* Registration/Tax ID */}
                {(branch.taxId || branch.registrationId) && (
                  <div className="flex items-center gap-3 text-sm">
                    <span className="text-zinc-500">
                      {branch.taxId ? "الرقم الضريبي:" : "رقم المنشأة:"}
                    </span>
                    <span className="text-zinc-300 font-mono" dir="ltr">
                      {branch.taxId || branch.registrationId}
                    </span>
                  </div>
                )}

                {/* Phone */}
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-zinc-500 shrink-0" />
                  <Link
                    href={`tel:${branch.phone}`}
                    className="text-sm text-zinc-400 hover:text-zinc-200 transition-colors"
                    dir="ltr"
                  >
                    {branch.phone}
                  </Link>
                </div>

                {/* WhatsApp */}
                <div className="flex items-start gap-3">
                  <MessageCircle className="w-5 h-5 text-green-500 shrink-0" />
                  <div className="flex flex-wrap gap-2">
                    {branch.whatsapp.map((num) => (
                      <Link
                        key={num}
                        href={`https://wa.me/${num.replace(/[^0-9]/g, "")}`}
                        target="_blank"
                        className="text-sm text-green-400 hover:text-green-300 transition-colors px-2 py-1 bg-green-900/20 rounded-lg"
                        dir="ltr"
                      >
                        {num}
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-zinc-500 shrink-0" />
                  <Link
                    href={`mailto:${branch.email}`}
                    className="text-sm text-zinc-400 hover:text-zinc-200 transition-colors"
                  >
                    {branch.email}
                  </Link>
                </div>

                {/* Website (for Saudi branch) */}
                {branch.website && (
                  <Link
                    href={branch.website}
                    target="_blank"
                    className="flex items-center justify-center gap-2 mt-4 py-3 px-4 bg-green-600 hover:bg-green-500 text-white font-medium rounded-xl transition-colors"
                  >
                    <span>زيارة موقع السعودية</span>
                    <ExternalLink className="w-4 h-4" />
                  </Link>
                )}

                {/* Social Links */}
                <div className="flex items-center gap-3 pt-4 border-t border-zinc-800/50">
                  {branch.social.facebook && (
                    <Link
                      href={branch.social.facebook}
                      target="_blank"
                      className="p-2 rounded-lg bg-zinc-800/50 hover:bg-blue-900/30 text-zinc-400 hover:text-blue-400 transition-colors"
                      aria-label="Facebook"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                      </svg>
                    </Link>
                  )}
                  {branch.social.instagram && (
                    <Link
                      href={branch.social.instagram}
                      target="_blank"
                      className="p-2 rounded-lg bg-zinc-800/50 hover:bg-pink-900/30 text-zinc-400 hover:text-pink-400 transition-colors"
                      aria-label="Instagram"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                      </svg>
                    </Link>
                  )}
                  {branch.social.tiktok && (
                    <Link
                      href={branch.social.tiktok}
                      target="_blank"
                      className="p-2 rounded-lg bg-zinc-800/50 hover:bg-zinc-700/50 text-zinc-400 hover:text-zinc-200 transition-colors"
                      aria-label="TikTok"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
                      </svg>
                    </Link>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
